import { useTicketModalStore } from "@/store/erp/ticket-soporte/useTicketModalStore";
import { ScrollableDialogProps } from "../../ui/Dialogs";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
  TextField,
} from "@mui/material";
import { useState } from "react";
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { IoCreate } from "react-icons/io5";
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { ROUTER_PATHS } from "@/shared/constants";
import {
  useBuscarClientes,
  useImportarClienteDesdeSoftnet,
} from "@/shared/actions/erp/tickets-soporte-cliente";
import type { IClienteBusqueda } from "@/shared/interfaces/erp/tickets-soporte-clientes/gestion-ticket.interface";

// Helpers de identificacion: el sub-flujo "importar desde Softnet" exige
// CI ecuatoriana (10 digitos) o RUC (13 digitos) completos. Tolera
// guiones, espacios y puntos como separadores.
const esIdentificacionValida = (raw: string): boolean => {
  const soloDigitos = (raw || '').replace(/[^0-9]/g, '');
  return soloDigitos.length === 10 || soloDigitos.length === 13;
};
const normalizarIdentificacion = (raw: string): string =>
  (raw || '').replace(/[^0-9]/g, '');

export default function FiltrosTicketSoporteDatos() {
  const isOpenModal = useTicketModalStore(s => s.isModalOpen);
  const setIsOpenModal = useTicketModalStore(s => s.setIsModalOpen);
  const navigate = useNavigate();

  // Campos de búsqueda
  const [nombre, setNombre] = useState('');
  const [identificacion, setIdentificacion] = useState('');
  const [direccion, setDireccion] = useState('');
  const [serieOnt, setSerieOnt] = useState('');
  const [ip, setIp] = useState('');
  const [contrato, setContrato] = useState('');

  // Estado de búsqueda activa
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTipo, setSearchTipo] = useState('todos');
  const [searchTriggered, setSearchTriggered] = useState(false);

  const { data: searchResult, isLoading, isRefetching } = useBuscarClientes({
    query: searchQuery,
    tipo: searchTipo,
    enabled: searchTriggered && searchQuery.length >= 3,
  });

  // Mutation para importar 1 cedula desde Softnet al vuelo si no esta en local.
  // El onSuccess del hook invalida ['buscar-clientes'], asi que la query
  // activa de arriba (useBuscarClientes) se refetchea sola y aparecen los
  // resultados sin necesidad de tocar searchQuery/searchTriggered.
  const importar = useImportarClienteDesdeSoftnet();

  // useBuscarClientes devuelve IClienteBusqueda[] directo en .data.
  const resultados: IClienteBusqueda[] = searchResult?.data || [];

  // ¿La busqueda actual fue por identificacion y la query es CI/RUC valida?
  // Solo en ese caso ofrecemos el sub-flujo de importar desde Softnet.
  const puedeImportarSoftnet =
    searchTipo === 'identificacion' && esIdentificacionValida(searchQuery);

  const handleImportarSoftnet = async () => {
    const ident = normalizarIdentificacion(searchQuery);
    if (!ident) return;
    try {
      await importar.mutateAsync(ident);
      // El hook invalida la cache; la query activa refetchea sola y aparecen
      // los resultados sin recargar el modal.
    } catch {
      // El hook ya muestra el alert correspondiente (404 / 502 / formato).
    }
  };

  const handleBuscar = () => {
    let query = '';
    let tipo = 'todos';

    if (identificacion) {
      query = identificacion;
      tipo = 'identificacion';
    } else if (nombre) {
      query = nombre;
      tipo = 'razon_social';
    } else if (contrato) {
      query = contrato;
      tipo = 'contrato';
    } else if (ip) {
      query = ip;
      tipo = 'ip';
    } else if (direccion) {
      query = direccion;
      tipo = 'direccion';
    } else if (serieOnt) {
      query = serieOnt;
      tipo = 'serie_ont';
    }

    if (!query || query.length < 3) {
      toast.warning('Ingrese al menos 3 caracteres en algún campo de búsqueda');
      return;
    }

    setSearchQuery(query);
    setSearchTipo(tipo);
    setSearchTriggered(true);
  };

  const handleLimpiar = () => {
    setNombre('');
    setIdentificacion('');
    setDireccion('');
    setSerieOnt('');
    setIp('');
    setContrato('');
    setSearchQuery('');
    setSearchTipo('todos');
    setSearchTriggered(false);
  };

  const handleClose = () => {
    setIsOpenModal(false);
    handleLimpiar();
  };

  const handleCrearTicket = (cliente: IClienteBusqueda) => {
    handleClose();
    navigate(
      `${ROUTER_PATHS.ticketSoporteCliente.nuevo_ticket_nav}?clienteId=${cliente.id}&contratoId=${cliente.contrato_id || ''}`
    );
  };

  const handleVerPerfil = (cliente: IClienteBusqueda) => {
    handleClose();
    navigate(
      `${ROUTER_PATHS.ticketSoporteCliente.nuevo_ticket_nav}?clienteId=${cliente.id}&contratoId=${cliente.contrato_id || ''}`
    );
  };

  return (
    <ScrollableDialogProps
      title="Búsqueda de Contratos"
      open={isOpenModal}
      onClose={handleClose}
      minWidth="70%"
      contentNode={
        <Box>
          <Grid container spacing={3}>
            {/* Columna 1: Datos Administrativos */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                DATOS ADMINISTRATIVOS
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                    Nombre y Apellido:
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Ingrese nombre y apellido"
                    value={nombre}
                    onChange={(e) => setNombre(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleBuscar(); }}
                    autoComplete="off"
                  />
                </Box>

                <Box>
                  <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                    Identificación:
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Ingrese identificación"
                    value={identificacion}
                    onChange={(e) => setIdentificacion(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleBuscar(); }}
                    type="text"
                    autoComplete="off"
                  />
                </Box>

                <Box>
                  <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                    Dirección:
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Ingrese dirección"
                    value={direccion}
                    onChange={(e) => setDireccion(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleBuscar(); }}
                    autoComplete="off"
                  />
                </Box>
              </Box>
            </Grid>

            {/* Columna 2: Datos Técnicos */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                DATOS TÉCNICOS
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                    Serie ONT:
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Ingrese serie ONT"
                    value={serieOnt}
                    onChange={(e) => setSerieOnt(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleBuscar(); }}
                    autoComplete="off"
                  />
                </Box>

                <Box>
                  <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                    IP:
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Ingrese IP"
                    value={ip}
                    onChange={(e) => setIp(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleBuscar(); }}
                    autoComplete="off"
                  />
                </Box>

                <Box>
                  <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                    Contrato (Fmax):
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Ingrese código de contrato"
                    value={contrato}
                    onChange={(e) => setContrato(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleBuscar(); }}
                    autoComplete="off"
                  />
                </Box>
              </Box>
            </Grid>

            {/* Botones */}
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ClearIcon />}
                onClick={handleLimpiar}
              >
                Limpiar
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleBuscar}
                endIcon={<SearchIcon />}
                disabled={!nombre && !identificacion && !direccion && !serieOnt && !ip && !contrato}
              >
                Buscar
              </Button>
            </Grid>

            {/* Loading */}
            {(isLoading || isRefetching) && (
              <Grid item xs={12}>
                <Box sx={{ width: '100%' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>
                    Buscando contratos...
                  </Typography>
                  <LinearProgress />
                </Box>
              </Grid>
            )}

            {/* Resultados */}
            {searchTriggered && !isLoading && !isRefetching && resultados.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Resultados ({resultados.length})
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 400, overflowY: 'auto' }}>
                  {resultados.map((cliente, idx) => (
                    <Paper
                      key={`${cliente.id}-${cliente.contrato_id}-${idx}`}
                      elevation={1}
                      sx={{
                        p: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="body2" fontWeight="bold">
                            {cliente.contrato_codigo ? `Contrato ${cliente.contrato_codigo}` : cliente.nombre_completo}
                          </Typography>
                          {cliente.tiene_masivo_activo && (
                            (cliente as any).masivo_estado === 'pausado'
                              ? <Chip label="ALERTA" size="small" color="warning" />
                              : <Chip label="MASIVO" size="small" color="error" />
                          )}
                          {cliente.estado && (
                            <Chip
                              label={cliente.estado}
                              size="small"
                              variant="outlined"
                              color={cliente.estado === 'ACTIVO' ? 'success' : 'default'}
                            />
                          )}
                          {cliente.plan && (
                            <Chip label={cliente.plan} size="small" variant="outlined" />
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {cliente.nombre_completo} — {cliente.cedula}
                        </Typography>
                        {cliente.direccion && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                            <LocationOnIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" fontWeight="bold">
                              {cliente.direccion}
                            </Typography>
                          </Box>
                        )}
                        {(cliente.ip || cliente.serie_ont) && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                            {cliente.ip ? `IP: ${cliente.ip}` : ''}
                            {cliente.ip && cliente.serie_ont ? ' | ' : ''}
                            {cliente.serie_ont ? `ONT: ${cliente.serie_ont}` : ''}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Crear Ticket">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleCrearTicket(cliente)}
                          >
                            <IoCreate />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Ver Perfil 360">
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => handleVerPerfil(cliente)}
                          >
                            <RemoveRedEyeIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              </Grid>
            )}

            {/* Sin resultados */}
            {searchTriggered && !isLoading && !isRefetching && resultados.length === 0 && searchQuery.length >= 3 && (
              <Grid item xs={12}>
                {puedeImportarSoftnet ? (
                  // Sub-flujo "importar desde Softnet": la busqueda fue por
                  // identificacion y la query es CI/RUC valida. Ofrecemos
                  // importarlo al vuelo desde el sistema legacy.
                  <Alert
                    severity="warning"
                    variant="outlined"
                    sx={{ alignItems: 'flex-start' }}
                  >
                    <AlertTitle sx={{ fontWeight: 700 }}>
                      No se encontro {normalizarIdentificacion(searchQuery)} en el sistema
                    </AlertTitle>
                    <Stack spacing={1.5} sx={{ mt: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Puedes traerlo desde Softnet. Se importaran todos los
                        datos del cliente (cliente, contratos, equipos e historial).
                      </Typography>
                      <Box>
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          startIcon={
                            importar.isPending ? (
                              <CircularProgress size={14} sx={{ color: 'inherit' }} />
                            ) : (
                              <CloudDownloadIcon />
                            )
                          }
                          disabled={importar.isPending}
                          onClick={handleImportarSoftnet}
                        >
                          {importar.isPending
                            ? 'Importando desde Softnet...'
                            : `Importar ${normalizarIdentificacion(searchQuery)} desde Softnet`}
                        </Button>
                      </Box>
                    </Stack>
                  </Alert>
                ) : (
                  // No se busco por identificacion o la query no es CI/RUC
                  // valida. Pedimos al usuario que ingrese la CI completa
                  // si quiere importar el cliente desde Softnet.
                  <Paper sx={{ p: 3 }}>
                    <Stack spacing={1.5} alignItems="center">
                      <Typography color="text.secondary" textAlign="center">
                        No se encontraron resultados para la busqueda
                      </Typography>
                      <Alert severity="info" variant="outlined" sx={{ width: '100%' }}>
                        <Typography variant="caption">
                          Si el cliente <strong>NO esta en el sistema</strong>, ingresa la{' '}
                          <strong>cedula (10 digitos)</strong> o <strong>RUC (13 digitos)</strong>{' '}
                          completos en el campo <strong>Identificacion</strong> para
                          importarlo desde Softnet.
                        </Typography>
                      </Alert>
                    </Stack>
                  </Paper>
                )}
              </Grid>
            )}
          </Grid>
        </Box>
      }
    />
  );
}
